import {
  WppActionButton,
  WppEmptyError,
  WppIconCross,
  WppIconDownload,
  WppSpinner,
  WppTooltip,
  WppTypography,
} from '@platform-ui-kit/components-library-react'
import { MayBeNull } from '@wpp-open/core'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDownloadFileApi } from 'api/files/queries/useDownloadFileApi'
import { Flex } from 'components/common/flex/Flex'
import { FullScreenModal } from 'components/surface/fullScreenModal/FullScreenModal'
import styles from 'pages/files/filePreviewModal/FilePreviewModal.module.scss'
import { FilePreviewType } from 'pages/files/types'
import { useDownloadFile } from 'pages/files/utils'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { createNiceModal, NiceModalWrappedProps } from 'utils/createNiceModal'

export interface Props extends NiceModalWrappedProps {
  type: FilePreviewType
  fileId: string
  fileName: string
}

const FilePreviewModal = ({ fileId, fileName, type, id, onClose, onCloseComplete, isOpen }: Props) => {
  const { t } = useTranslation()
  const { currentTenant } = useCurrentTenantData()
  const download = useDownloadFile()
  const [fileUrl, setFileUrl] = useState<MayBeNull<string>>(null)
  const [isPreviewError, setIsPreviewError] = useState(false)

  const {
    data: fileBlob,
    isFetching,
    isError,
  } = useDownloadFileApi({
    params: {
      fileId,
      tenantId: currentTenant.id,
    },
    staleTime: 10 * 60 * 1000,
  })

  const isLoading = !fileUrl

  useEffect(() => {
    // Only create an url when done fetching to avoid content reload after stale data is replaced
    if (!isFetching && fileBlob) {
      const newFileUrl = window.URL.createObjectURL(fileBlob)

      setFileUrl(newFileUrl)

      return () => {
        window.URL.revokeObjectURL(newFileUrl)
      }
    }
  }, [fileBlob, isFetching])

  useEffect(() => {
    if (isError) {
      setIsPreviewError(true)
    }
  }, [isError])

  return (
    <FullScreenModal
      className={styles.modal}
      open={isOpen}
      onWppFullScreenModalClose={onClose}
      onWppFullScreenModalCloseComplete={onCloseComplete}
      data-testid={id}
    >
      <Flex slot="header" align="center" justify="between" gap={16}>
        <WppTypography type="2xl-heading">{fileName}</WppTypography>
        <Flex align="center" gap={8}>
          <WppTooltip text={t('os.files.actions.download')}>
            <WppActionButton
              variant="secondary"
              onClick={() => download(fileId, fileName)}
              data-testid="file-preview-download"
            >
              <WppIconDownload slot="icon-start" />
            </WppActionButton>
          </WppTooltip>
          <WppActionButton variant="secondary" onClick={onClose}>
            <WppIconCross slot="icon-start" />
          </WppActionButton>
        </Flex>
      </Flex>

      <Flex slot="body" className={styles.modalBody} direction="column" gap={24}>
        {isPreviewError ? (
          <Flex className={styles.fullPage} direction="column" justify="center" align="center" gap={16}>
            <WppEmptyError />
            <WppTypography type="l-strong">{t('os.files.file_preview_modal.preview_error')}</WppTypography>
          </Flex>
        ) : (
          <>
            {isLoading ? (
              <Flex className={styles.fullPage} justify="center" align="center">
                <WppSpinner size="l" />
              </Flex>
            ) : (
              <>
                {type === FilePreviewType.PDF && <iframe className={styles.fullPage} src={fileUrl} />}
                {type === FilePreviewType.Image && (
                  <img
                    className={styles.imgPreview}
                    src={fileUrl}
                    alt={fileName}
                    onError={() => setIsPreviewError(true)}
                    data-testid="file-preview-image"
                  />
                )}
              </>
            )}
          </>
        )}
      </Flex>
    </FullScreenModal>
  )
}

export const { showModal: showFilePreviewModal } = createNiceModal(FilePreviewModal, 'file-preview-modal')
